import { createFileRoute, Link, redirect, useParams } from '@tanstack/react-router'
import { Badge } from '@thom/folio-ui/components/ui/badge'
import { Card } from '@journ/bloom/card'
import { StaggerItem } from '@thom/folio-ui/components/motion/stagger'
import { useDomain } from '@thom/folio-ui/app/use-domain'
import { RecordGone } from '@thom/folio-ui/components/record/record-gone'
import { EmptyState } from '@thom/folio-ui/components/empty-state'
import { useProjects } from '@thom/folio-ui/app/use-projects'
import { MemberRoster } from '@thom/folio-ui/pages/domain/member-roster'
import { Status } from '@thom/folio-domain/lib/async-status'
import { LoadError } from '@thom/folio-ui/components/load-error'

const DomainPage = () => {
	const { client, domain: slug } = useParams({ from: '/_authenticated/$client/$domain/' })
	const domain = useDomain(client, slug)
	const state = useProjects(
		domain.status === Status.Ready ? { domainId: domain.domain.id } : undefined,
		domain.status !== Status.Ready,
	)

	if (domain.status === Status.Failed) return <LoadError message={domain.message} />
	if (domain.status === Status.Gone) {
		return (
			<RecordGone title={domain.title}>
				<Link to='/$client' params={{ client }} className='text-sm underline'>
					Back to {client}
				</Link>
			</RecordGone>
		)
	}

	return (
		<div className='space-y-6'>
			<div className='space-y-1'>
				<h1 className='font-serif text-3xl'>{domain.status === Status.Ready ? domain.domain.name : slug}</h1>
				{domain.status === Status.Ready && domain.domain.descr && (
					<p className='text-dim text-sm'>{domain.domain.descr}</p>
				)}
			</div>

			{domain.status === Status.Ready && <MemberRoster members={domain.domain.members} />}

			{state.status === Status.Failed && <LoadError message={state.message} />}

			{state.status === Status.Ready && state.projects.length === 0 && (
				<EmptyState
					message='No projects in this domain yet.'
					command={`folio project create "<name>" --domain ${client}/${slug}`}
				/>
			)}

			{state.status === Status.Ready && state.projects.length > 0 && (
				<div className='grid gap-4 sm:grid-cols-2'>
					{state.projects.map((project, index) => (
						<StaggerItem key={project.id} index={index}>
							<Link to='/$client/$domain/$slug' params={{ client, domain: slug, slug: project.slug }} className='block'>
								<Card.Root className='hover:bg-accent/40 gap-1.5 transition-colors'>
									<div className='flex items-start justify-between gap-4'>
										<Card.Title className='mb-2 leading-none'>{project.name}</Card.Title>
										{project.archived && <Badge color='muted'>Archived</Badge>}
									</div>

									<Card.Description>{project.descr || 'No description.'}</Card.Description>

									<div className='text-dimmer flex items-center gap-2 pt-2 text-xs'>
										<span>{project.slug}</span>
									</div>
								</Card.Root>
							</Link>
						</StaggerItem>
					))}
				</div>
			)}
		</div>
	)
}

export const Route = createFileRoute('/_authenticated/$client/$domain/')({
	beforeLoad: async ({ context, params }) => {
		const domain = await context.container.domains.get(params.client, params.domain)
		if (!domain.success) return

		const projects = await context.container.projects.list({ domainId: domain.value.id })
		const [only] = projects.success && projects.value.length === 1 ? projects.value : []
		if (only) throw redirect({ to: '/$client/$domain/$slug', params: { ...params, slug: only.slug } })
	},
	component: DomainPage,
})
